import {
  buildAcademicGradeEntryProgress,
  deriveAcademicGradeEntryStudentStatus,
  formatAcademicCompositeGrade,
  type AcademicGradeEntryMutationEntry,
  type AcademicGradeEntryStudent,
  type AcademicGradeEntryWorkspace,
} from '@triedge/academics-domain';

export const SOURCE_FAITHFUL_GRADE_ENTRY_QUERY = {
  routeKey: 'school-academics-source-faithful-grade-entry',
} as const;

export const createDraftEntriesFromWorkspace = (
  workspace: AcademicGradeEntryWorkspace,
): AcademicGradeEntryMutationEntry[] => workspace.students.map((student) => ({
  studentKey: student.studentKey,
  baseGrade: student.baseGrade,
  modifier: student.baseGrade === 'F' ? 'none' : student.modifier,
  comment: student.comment ?? null,
}));

export const buildWorkspaceWithDraftEntries = (
  workspace: AcademicGradeEntryWorkspace,
  entries: readonly AcademicGradeEntryMutationEntry[],
  workflowState?: AcademicGradeEntryWorkspace['workflowState'],
): AcademicGradeEntryWorkspace => {
  const entryMap = new Map(entries.map((entry) => [entry.studentKey, entry]));
  const students = workspace.students.map((student) => {
    const override = entryMap.get(student.studentKey);
    const baseGrade = override?.baseGrade ?? student.baseGrade;
    const modifier = baseGrade === 'F'
      ? 'none'
      : override?.modifier ?? student.modifier;
    const comment = override?.comment ?? student.comment ?? null;

    return {
      ...student,
      baseGrade,
      modifier,
      grade: formatAcademicCompositeGrade(baseGrade, modifier),
      comment,
      hasComment: Boolean(comment?.trim()),
      status: deriveAcademicGradeEntryStudentStatus({ baseGrade }),
    } satisfies AcademicGradeEntryStudent;
  });

  return {
    ...workspace,
    workflowState: workflowState ?? workspace.workflowState,
    updatedAt: new Date().toISOString(),
    progress: buildAcademicGradeEntryProgress(students),
    students,
  };
};

export const diffGradeEntryDraftEntries = (
  workspace: AcademicGradeEntryWorkspace,
  entries: readonly AcademicGradeEntryMutationEntry[],
): AcademicGradeEntryMutationEntry[] => {
  const baseline = new Map(
    createDraftEntriesFromWorkspace(workspace).map((entry) => [entry.studentKey, entry]),
  );
  return entries.filter((entry) => {
    const previous = baseline.get(entry.studentKey);
    return (
      previous?.baseGrade !== entry.baseGrade
      || previous?.modifier !== entry.modifier
      || (previous?.comment ?? null) !== (entry.comment ?? null)
    );
  });
};

export const getDefaultExpandedStudentKey = (
  workspace: AcademicGradeEntryWorkspace,
): string | null => (
  workspace.students.find((student) => student.status !== 'normal')?.studentKey
  ?? workspace.students[0]?.studentKey
  ?? null
);
